import { ExternalRoute } from "@/constants/externalRoute.ts"
import {
  ENABLED_LINKS_ON_CLOSED_ACCOUNT,
  LOGGED_IN_NAVIGATION_ITEMS,
  LOGGED_IN_TABLET_NAVIGATION_ITEMS,
  LOGGED_OUT_NAVIGATION_ITEMS,
  LOGGED_OUT_TABLET_NAVIGATION_ITEMS,
  MOBILE_NAVIGATION_ITEMS,
  SIDEBAR_NAVIGATION_ITEMS,
} from "@/constants/navigation/navigation.ts"
import { NAV_LABELS } from "@/constants/navigation/navigationTypes.ts"
import { SMART_PAY_AS_YOU_GO_NAVIGATION } from "@/constants/navigation/smartPayAsYouGoNavigation.ts"
import { MyAccountRoute as InternalRoute } from "@/constants/routes/myAccount.ts"
import { useNavConfig } from "@/lib/shared/useNavConfig.ts"
import { NavbarItem } from "@/types/Navbar"
import { computed, unref } from "vue"

type Config = {
  isTraditionalSmartEligible: boolean
  isWHDEligible: boolean
  isAccountClosed: boolean
  isTerminating: boolean
  isLeaving: boolean
  isAuthenticated: boolean
  hasMoveOut: boolean
  isSmartPayAsYouGoOn: boolean
  hasFlexMarketSubscription: boolean
  showSoCharged: boolean
  isSmartChargingEnrolled: boolean
  isMoveWithUsEnabled: boolean
}

export function useNavigationItems() {
  const { navConfig, isLoading } = useNavConfig()

  const sidebarNavigationItems = computed(() =>
    filterMainNavigationItems(
      navConfig.value.isSmartPayAsYouGoOn
        ? SMART_PAY_AS_YOU_GO_NAVIGATION.SIDEBAR
        : SIDEBAR_NAVIGATION_ITEMS,
      unref(navConfig),
    ),
  )

  const navigationItems = computed(() => {
    let items = LOGGED_OUT_NAVIGATION_ITEMS
    if (navConfig.value.isSmartPayAsYouGoOn) {
      items = SMART_PAY_AS_YOU_GO_NAVIGATION.TOP
    } else if (navConfig.value.isAuthenticated) {
      items = LOGGED_IN_NAVIGATION_ITEMS
    }
    return filterMainNavigationItems(items, unref(navConfig))
  })

  const extendedNavigationItems = computed(() => {
    let items = LOGGED_OUT_TABLET_NAVIGATION_ITEMS
    if (navConfig.value.isSmartPayAsYouGoOn) {
      items = SMART_PAY_AS_YOU_GO_NAVIGATION.TABLET
    } else if (navConfig.value.isAuthenticated) {
      items = LOGGED_IN_TABLET_NAVIGATION_ITEMS
    }
    return filterMainNavigationItems(items, unref(navConfig))
  })

  const mobileNavigationItems = computed(() => {
    let items = MOBILE_NAVIGATION_ITEMS
    if (navConfig.value.isSmartPayAsYouGoOn) {
      items = SMART_PAY_AS_YOU_GO_NAVIGATION.MOBILE
    }
    return unref(navConfig.value.isAuthenticated)
      ? filterMainNavigationItems(items, unref(navConfig))
      : unref(navigationItems)
  })

  return {
    sidebarNavigationItems,
    navigationItems,
    extendedNavigationItems,
    mobileNavigationItems,
    isLoading,
  }
}

const filterMainNavigationItems = (
  items: NavbarItem[],
  config: Config,
): NavbarItem[] => {
  const {
    isTraditionalSmartEligible,
    isWHDEligible,
    isAccountClosed,
    isTerminating,
    isLeaving,
    isAuthenticated,
    hasMoveOut,
    hasFlexMarketSubscription,
    showSoCharged,
    isSmartChargingEnrolled,
    isMoveWithUsEnabled,
  } = config

  const filterItem = (item: NavbarItem): boolean => {
    switch (item.label) {
      case NAV_LABELS.account:
        return isAuthenticated
      case NAV_LABELS.smartMeterBooking:
        return isTraditionalSmartEligible && !isTerminating
      case NAV_LABELS.warmHomeDiscount:
        return isWHDEligible
      case NAV_LABELS.flexMarketSubscription:
        return hasFlexMarketSubscription && showSoCharged
      case NAV_LABELS.evCharging:
        return isSmartChargingEnrolled
      default:
        return true
    }
  }
  const processItems = (items: NavbarItem[]): NavbarItem[] => {
    return items.filter(filterItem).map((item) => {
      const processedItem: NavbarItem = { ...item }

      if (item.children?.length) {
        processedItem.children = processItems(item.children)

        processedItem.isDisabled = processedItem.children.every(
          (child) => child.isDisabled,
        )
      }

      if (isAccountClosed) {
        processedItem.isDisabled = item.to
          ? !ENABLED_LINKS_ON_CLOSED_ACCOUNT.includes(item.to)
          : true
      } else if (
        isLeaving &&
        (item.to === InternalRoute.rewards ||
          item.to === InternalRoute.referAFriend)
      ) {
        processedItem.isDisabled = true
      }

      if (
        hasMoveOut &&
        processedItem.label === NAV_LABELS.movingHome &&
        !isMoveWithUsEnabled
      ) {
        processedItem.to = ExternalRoute.moveOutStatus
      }

      return processedItem
    })
  }

  return processItems(items)
}
